import React, { useState } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import onboardingService from '../../services/onboardingService';
import { errText } from '../../services/scheduleService';

/**
 * Invite a patient to complete their own details: name + email → a single-use
 * link (7 days) to this practice's onboarding page. If the email can't be sent,
 * the link is shown so it can be shared another way.
 */
const InvitePatientModal = ({ onClose, onSent }) => {
  const [form, setForm] = useState({ first_names: '', surname: '', email: '', cell_phone: '' });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors([]);
    try {
      const r = await onboardingService.invite(form);
      setResult(r);
      onSent();
    } catch (ex) {
      setErrors(ex.response?.data?.errors || [errText(ex)]);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch {
      window.prompt('Copy this link:', result.link);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-brand-deep/50 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-brand-deep">Invite a patient</h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        {result ? (
          <div className="flex flex-col gap-3 text-sm">
            {result.email.sent ? (
              <p className="flex items-start gap-2 rounded-lg bg-green-50 px-3 py-2.5 text-green-800">
                <CheckCircleIcon className="h-5 w-5 shrink-0" />
                <span>Sent to <strong>{result.invite.email}</strong>. The link works once and expires on {new Date(result.invite.expires_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })}.</span>
              </p>
            ) : (
              <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-amber-800">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                <span>The link was made but the email wasn’t sent: {result.email.error} Copy the link below and share it with the patient another way.</span>
              </p>
            )}
            <div className="flex gap-2">
              <input readOnly value={result.link} className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-slate-50 p-2 font-mono text-xs text-slate-600" onFocus={(e) => e.target.select()} />
              <button onClick={copy} className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-brand-navy px-3 text-xs font-semibold text-brand-navy hover:bg-slate-50">
                <ClipboardDocumentIcon className="h-4 w-4" /> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-slate-500">When the patient sends their details, they appear under Patient onboarding for review.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setResult(null); setCopied(false); setForm({ first_names: '', surname: '', email: '', cell_phone: '' }); }} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Invite another</button>
              <button onClick={onClose} className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={send} className="flex flex-col gap-3">
            <p className="text-sm text-slate-500">
              The patient gets an email with a secure link to enter their ID and medical aid details and upload their ID and medical aid card. You review what they send before it’s used.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name *"><input className={inputCls} value={form.first_names} onChange={(e) => set('first_names', e.target.value)} autoFocus /></Field>
              <Field label="Surname *"><input className={inputCls} value={form.surname} onChange={(e) => set('surname', e.target.value)} /></Field>
            </div>
            <Field label="Email *"><input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
            <Field label="Cell (optional)"><input className={inputCls} value={form.cell_phone} onChange={(e) => set('cell_phone', e.target.value)} placeholder="Kept on the invite for WhatsApp later" /></Field>
            {errors.length > 0 && <ul className="text-sm text-red-600">{errors.map((er) => <li key={er}>{er}</li>)}</ul>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={busy} className="rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">
                {busy ? 'Sending…' : 'Send link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const inputCls = 'w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';
const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[0.8rem] font-semibold text-slate-600">{label}</span>
    {children}
  </label>
);

export default InvitePatientModal;
